import React from 'react';

import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { trimObjectValues } from '@hrlogics/helper-utils';
import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { appRoutes } from '@clearverify/constants';

import type { LoginResultWithEmail } from 'src/components/auth/Login/types';
import { Link } from 'src/components/common';
import { PasswordField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useLoginRedirectTarget } from 'src/hooks/auth';
import { UserNotProvisionedError } from 'src/providers/AuthProvider';
import type { SxProp } from 'src/types/mui';

import { LoginFormField, LoginFormValues } from './types';
import { loginValidationSchema } from './utils/validationSchemas';

type LoginFormProps = {
  loginData: LoginResultWithEmail;
  handleLoginResult: (data: LoginResultWithEmail) => void;
  isLoginDisabled: boolean;
  switchLoginDisabledState: (isDisabled: boolean) => void;
};

export const LoginForm: React.FC<LoginFormProps> = ({
  loginData,
  handleLoginResult,
  isLoginDisabled,
  switchLoginDisabledState,
}) => {
  const navigate = useNavigate();

  const { login } = useAppAuth();
  const { spawnNotification } = useNotification();
  const redirectTarget = useLoginRedirectTarget();

  const initialValues: Partial<LoginFormValues> = {
    email: loginData.email,
  };

  const submitHandler = React.useCallback(
    async (rawFormValues: LoginFormValues) => {
      switchLoginDisabledState(true);
      try {
        const formValues = trimObjectValues(rawFormValues);

        const data = await login({ ...formValues });
        handleLoginResult({ ...data, email: formValues.email });

        if (data.inactive) {
          spawnNotification('Login Error: Your account has been deactivated.', 'error');
        }

        if (data.success && !data.session) {
          navigate(redirectTarget, { replace: true });
        }
      } catch (error) {
        console.error(error);
        if (error instanceof UserNotProvisionedError) {
          spawnNotification(error.message, 'error', 'Access Denied');
        } else if (error instanceof Error) {
          spawnNotification('Incorrect username or password', 'error', 'User Authentication Error');
        }
      } finally {
        switchLoginDisabledState(false);
      }
    },
    [
      switchLoginDisabledState,
      handleLoginResult,
      login,
      navigate,
      redirectTarget,
      spawnNotification,
    ],
  );

  return (
    <React.Fragment>
      {!loginData.success && !loginData.inactive && (
        <Alert severity="error">Email or password is invalid</Alert>
      )}

      <Form<LoginFormValues>
        initialValues={initialValues}
        onSubmit={submitHandler}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ sx: formStyles }}
        validate={validateWithYupSchema(loginValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Field
                name={LoginFormField.email}
                component={TextField}
                fullWidth
                label="Email"
                disabled={submitting || isLoginDisabled}
                slotProps={{
                  input: {
                    autoComplete: 'email',
                  },
                }}
              />

              <Field
                name={LoginFormField.password}
                component={PasswordField}
                fullWidth
                label="Password"
                disabled={submitting || isLoginDisabled}
                slotProps={{
                  input: {
                    autoComplete: 'current-password',
                  },
                }}
              />

              <Typography variant="body1" color="text.secondary" mt={-1}>
                Forgot password? <Link to={appRoutes.auth.passwordReset}>Click here</Link>
              </Typography>

              {!!loginData.inactive && (
                <Alert severity="error" variant="outlined">
                  Your account was deactivated. Contact your organization
                </Alert>
              )}

              <Box textAlign="center" mt={2}>
                <Button
                  type="submit"
                  disabled={submitting || !valid || isLoginDisabled}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Sign In
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};

const formStyles = {
  display: 'grid',
  gap: 2,
} as const satisfies SxProp;
